import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { submitEnquiry } from "../services/contactService";

// Contact Page with WordPress Custom REST API & PHP Fallback Integration
const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course: '',
    message: '',
    website: '' // Anti-spam honeypot
  });

  const [formStatus, setFormStatus] = useState({
    loading: false,
    success: false,
    error: null,
    message: ''
  });

  const [fieldErrors, setFieldErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear field error on change
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    setFormStatus({ loading: true, success: false, error: null, message: '' });
    setFieldErrors({});

    // Submit via contactService (WordPress REST API POST /wp-json/dimensionias/v1/contact)
    const result = await submitEnquiry(formData);

    if (result.success) {
      setFormStatus({
        loading: false,
        success: true,
        error: null,
        message: result.message || 'Your enquiry has been submitted successfully. Our team will contact you shortly.'
      });

      // Reset form on success
      setFormData({
        name: '',
        email: '',
        phone: '',
        course: '',
        message: '',
        website: ''
      });

      // Auto-hide success message after 7 seconds
      setTimeout(() => {
        setFormStatus(prev => ({ ...prev, success: false }));
      }, 7000);
    } else {
      setFormStatus({
        loading: false,
        success: false,
        error: result.message || 'Failed to submit enquiry. Please try again.',
        message: ''
      });

      if (result.errors) {
        setFieldErrors(result.errors);
      }
    }
  };

  return (
    <div className="min-h-screen pt-20 sm:pt-24 pb-10 sm:pb-12" style={{ backgroundColor: '#F8FAF9' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Page Header */}
        <div className="text-center max-w-3xl mx-auto mb-6 sm:mb-8">
          <div className="inline-block px-4 py-1.5 rounded-full bg-emerald-100 text-[#134E4A] font-bold text-xs uppercase tracking-widest mb-4">
            GET IN TOUCH
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-slate-900 mb-4 tracking-tight">
            Contact & Admission Enquiry
          </h1>
          <p className="text-slate-600 font-semibold text-lg">
            Have questions about our UPSC, CAPF, or MPSC programs? Send us a message or schedule a personal mentorship session.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 items-start">
          
          {/* Contact Details Column */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-200/80 space-y-6">
              <h2 className="text-2xl font-black text-[#134E4A]">Contact Information</h2>
              <p className="text-sm font-medium text-slate-600 leading-relaxed">
                Our main office is located in Pune. Visit us for one-on-one counselling or reach out directly through phone or email.
              </p>

              <div className="space-y-5 pt-2">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-2xl bg-emerald-50 text-[#134E4A] shrink-0">
                    <Phone size={22} />
                  </div>
                  <div>
                    <span className="block text-xs font-bold uppercase text-slate-400">Phone & WhatsApp</span>
                    <a href="tel:+917530807530" className="text-base font-extrabold text-slate-900 hover:text-[#134E4A] transition-colors">
                      +91 7530807530
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-2xl bg-emerald-50 text-[#134E4A] shrink-0">
                    <Mail size={22} />
                  </div>
                  <div>
                    <span className="block text-xs font-bold uppercase text-slate-400">Official Email</span>
                    <a href="mailto:contact@dimensionias.com" className="text-base font-extrabold text-slate-900 hover:text-[#134E4A] transition-colors">
                      contact@dimensionias.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-2xl bg-emerald-50 text-[#134E4A] shrink-0">
                    <MapPin size={22} />
                  </div>
                  <div>
                    <span className="block text-xs font-bold uppercase text-slate-400">Head Office</span>
                    <p className="text-sm font-bold text-slate-800 leading-snug">
                      Dimension IAS Academy, Pune, Maharashtra, India
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-2xl bg-emerald-50 text-[#134E4A] shrink-0">
                    <Clock size={22} />
                  </div>
                  <div>
                    <span className="block text-xs font-bold uppercase text-slate-400">Working Hours</span>
                    <p className="text-sm font-bold text-slate-800">
                      Monday – Saturday: 9:00 AM – 7:30 PM
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Enquiry Form Column */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-xl border border-slate-200/80">
              <h2 className="text-2xl font-black text-slate-900 mb-2">Send an Enquiry</h2>
              <p className="text-sm text-slate-500 font-medium mb-8">
                Fill out the form below. An email notification will be sent directly to <strong className="text-[#134E4A]">contact@dimensionias.com</strong> and you will receive an automatic confirmation email.
              </p>

              {/* Status Feedback Banners */}
              {formStatus.success && (
                <div className="mb-6 p-4 rounded-2xl bg-emerald-50 border border-emerald-300 text-[#134E4A] flex items-center gap-3">
                  <CheckCircle size={22} className="shrink-0 text-emerald-600" />
                  <span className="text-sm font-bold">{formStatus.message}</span>
                </div>
              )}

              {formStatus.error && (
                <div className="mb-6 p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 flex items-center gap-3">
                  <AlertCircle size={22} className="shrink-0 text-rose-600" />
                  <span className="text-sm font-bold">{formStatus.error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* Anti-Spam Honeypot Field */}
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  tabIndex="-1"
                  autoComplete="off"
                  className="hidden"
                  aria-hidden="true"
                />

                {/* Name Field */}
                <div>
                  <label className="block text-xs font-extrabold uppercase tracking-wider text-slate-700 mb-2">
                    Full Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={formStatus.loading}
                    placeholder="e.g. Rahul Sharma"
                    className={`w-full px-4 py-3.5 rounded-xl border-2 font-semibold text-slate-800 transition-all outline-none ${
                      fieldErrors.name ? 'border-rose-400 bg-rose-50/50' : 'border-slate-200 bg-slate-50 focus:bg-white focus:border-[#134E4A]'
                    }`}
                  />
                  {fieldErrors.name && (
                    <p className="text-xs font-bold text-rose-600 mt-1">{fieldErrors.name}</p>
                  )}
                </div>

                {/* Email & Phone Grid */}
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-extrabold uppercase tracking-wider text-slate-700 mb-2">
                      Email Address <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={formStatus.loading}
                      placeholder="e.g. rahul@gmail.com"
                      className={`w-full px-4 py-3.5 rounded-xl border-2 font-semibold text-slate-800 transition-all outline-none ${
                        fieldErrors.email ? 'border-rose-400 bg-rose-50/50' : 'border-slate-200 bg-slate-50 focus:bg-white focus:border-[#134E4A]'
                      }`}
                    />
                    {fieldErrors.email && (
                      <p className="text-xs font-bold text-rose-600 mt-1">{fieldErrors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-extrabold uppercase tracking-wider text-slate-700 mb-2">
                      Phone Number <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      disabled={formStatus.loading}
                      placeholder="e.g. 9876543210"
                      className={`w-full px-4 py-3.5 rounded-xl border-2 font-semibold text-slate-800 transition-all outline-none ${
                        fieldErrors.phone ? 'border-rose-400 bg-rose-50/50' : 'border-slate-200 bg-slate-50 focus:bg-white focus:border-[#134E4A]'
                      }`}
                    />
                    {fieldErrors.phone && (
                      <p className="text-xs font-bold text-rose-600 mt-1">{fieldErrors.phone}</p>
                    )}
                  </div>
                </div>

                {/* Course Selection Field */}
                <div>
                  <label className="block text-xs font-extrabold uppercase tracking-wider text-slate-700 mb-2">
                    Program / Course of Interest
                  </label>
                  <select
                    name="course"
                    value={formData.course}
                    onChange={handleInputChange}
                    disabled={formStatus.loading}
                    className="w-full px-4 py-3.5 rounded-xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-[#134E4A] font-semibold text-slate-800 transition-all outline-none"
                  >
                    <option value="">Select a Course (Optional)</option>
                    <option value="UPSC Integrated Foundation Batch">UPSC Integrated Foundation Batch</option>
                    <option value="CAPF AC Intensive Mentorship">CAPF AC Intensive Mentorship</option>
                    <option value="MPSC Comprehensive Batch">MPSC Comprehensive Batch</option>
                    <option value="UPSC Prelims & Mains Test Series">UPSC Prelims & Mains Test Series</option>
                    <option value="Personalized Mentorship Program">Personalized Mentorship Program</option>
                    <option value="Essay & Ethics Special Module">Essay & Ethics Special Module</option>
                  </select>
                </div>

                {/* Message Field */}
                <div>
                  <label className="block text-xs font-extrabold uppercase tracking-wider text-slate-700 mb-2">
                    Message / Questions <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleInputChange}
                    disabled={formStatus.loading}
                    placeholder="Tell us about your preparation goals and any specific questions..."
                    className={`w-full px-4 py-3.5 rounded-xl border-2 font-semibold text-slate-800 transition-all outline-none resize-none ${
                      fieldErrors.message ? 'border-rose-400 bg-rose-50/50' : 'border-slate-200 bg-slate-50 focus:bg-white focus:border-[#134E4A]'
                    }`}
                  ></textarea>
                  {fieldErrors.message && (
                    <p className="text-xs font-bold text-rose-600 mt-1">{fieldErrors.message}</p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={formStatus.loading}
                  className="w-full bg-[#134E4A] text-white font-extrabold text-base py-4 rounded-xl shadow-lg hover:bg-[#0F3D39] transition-all flex items-center justify-center gap-2.5 disabled:opacity-60 disabled:cursor-not-allowed transform hover:-translate-y-0.5"
                >
                  {formStatus.loading ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      <span>Submitting Enquiry...</span>
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      <span>Submit Enquiry</span>
                    </>
                  )}
                </button>

              </form>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default Contact;